import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, RefreshCw, Copy, Send, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  new:        { label: "NEW",         color: "bg-blue-50 text-blue-700 border-blue-200" },
  code_ready: { label: "CODE READY",  color: "bg-amber-50 text-amber-700 border-amber-200" },
  code_sent:  { label: "CODE SENT",   color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/**
 * Marketing lead pipeline: landing-page white-paper requests → approve →
 * beta code minted + emailed → the invitee registers. `signed_up` is
 * computed server-side from the users collection so admins see the whole
 * funnel in one view.
 */
export default function AdminMarketingLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [approveForm, setApproveForm] = useState({
    make_admin: false, roles: [], expires_in_days: 14, send_email: true,
  });
  const [freshCode, setFreshCode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, r] = await Promise.all([
        api.get("/admin/marketing-leads"),
        api.get("/admin/roles"),
      ]);
      setLeads(l.data);
      setAvailableRoles(r.data.roles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load marketing leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openApprove = (lead) => {
    setTarget(lead);
    setApproveForm({
      make_admin: false, roles: ["QA Lead"], expires_in_days: 14, send_email: true,
    });
    setApproveOpen(true);
  };

  const toggleRole = (r) => setApproveForm((f) => ({
    ...f,
    roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
  }));

  const submitApprove = async () => {
    if (!target) return;
    try {
      const { data } = await api.post(`/admin/marketing-leads/${target.lead_id}/approve`, approveForm);
      setFreshCode({ ...data, email: target.email });
      setApproveOpen(false);
      if (data.email_sent) {
        toast.success(`Code minted &amp; emailed to ${target.email}`);
      } else {
        toast.info("Code minted — email not sent (Resend not configured). Copy the code from the modal.", { duration: 8000 });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied — share it via your preferred channel");
  };

  return (
    <div className="mt-12" data-testid="admin-marketing-leads-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vq-overline mb-2">Marketing pipeline</div>
          <h2 className="font-display font-bold text-2xl text-slate-900">Early-access lead requests</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Everyone who filled out the landing-page &ldquo;Request early access&rdquo; form. Click <strong>Approve</strong> to mint a single-use beta code with pre-assigned roles and email it directly to them.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-leads-refresh"
                className="h-11 border-slate-300 font-semibold">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Lead</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Company</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Interest</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const status = l.signed_up ? "signed_up" : (l.status || "new");
              const meta = l.signed_up
                ? { label: "SIGNED UP", color: "bg-indigo-50 text-indigo-700 border-indigo-200" }
                : (STATUS_META[status] || STATUS_META.new);
              return (
                <tr key={l.lead_id} className="border-b border-slate-100" data-testid={`admin-lead-row-${l.lead_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-display font-bold text-sm text-slate-900">{l.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{l.email}</div>
                    {l.phone && <div className="text-[10px] font-mono text-slate-400">{l.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-slate-800">{l.company}</div>
                    {l.role_title && <div className="text-[10px] font-mono text-slate-500">{l.role_title}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-sm">
                    <div className="whitespace-pre-wrap line-clamp-3">{l.interest || <span className="text-slate-400">—</span>}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-1">
                      {new Date(l.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] border ${meta.color}`}>
                      {meta.label}
                    </span>
                    {l.beta_code_issued && (
                      <div className="mt-1 text-[10px] font-mono text-slate-500 break-all">
                        {l.beta_code_issued}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!l.beta_code_issued && !l.signed_up && (
                      <Button size="sm" onClick={() => openApprove(l)} data-testid={`admin-lead-approve-${l.lead_id}`}
                              className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Approve
                      </Button>
                    )}
                    {l.beta_code_issued && !l.signed_up && (
                      <Button size="sm" variant="outline" onClick={() => copyCode(l.beta_code_issued)}
                              data-testid={`admin-lead-copy-code-${l.lead_id}`}
                              className="border-slate-300 font-semibold">
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy code
                      </Button>
                    )}
                    {l.signed_up && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" /> Activated
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {leads.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">
                No early-access requests yet. Once your landing page starts converting, they&apos;ll appear here.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Approve {target?.name}</DialogTitle>
            <DialogDescription>
              Mints a single-use beta code, {approveForm.send_email ? "emails it directly to " : "displays it for you to send to "}
              <span className="font-mono">{target?.email}</span>, and pre-assigns the roles below on their first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Pre-assigned roles</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1 border border-slate-200 rounded p-2 max-h-52 overflow-y-auto">
                {availableRoles.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm p-1">
                    <Checkbox data-testid={`approve-role-${r.replace(/\s/g, "-")}`}
                              checked={approveForm.roles.includes(r)}
                              onCheckedChange={() => toggleRole(r)} />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="approve-make-admin"
                        checked={approveForm.make_admin}
                        onCheckedChange={(v) => setApproveForm({ ...approveForm, make_admin: !!v })} />
              Grant Admin on signup (super user)
            </label>
            <div>
              <Label className="text-xs font-semibold">Code expires in (days)</Label>
              <Input type="number" min={1} max={90} value={approveForm.expires_in_days}
                     data-testid="approve-expires-days"
                     onChange={(e) => setApproveForm({ ...approveForm, expires_in_days: parseInt(e.target.value) || 14 })}
                     className="mt-1.5" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="approve-send-email"
                        checked={approveForm.send_email}
                        onCheckedChange={(v) => setApproveForm({ ...approveForm, send_email: !!v })} />
              Email the code directly to the lead (requires Resend key)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={submitApprove} data-testid="approve-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">
              <Send className="w-4 h-4 mr-2" /> Mint code &amp; {approveForm.send_email ? "send" : "close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fresh code — show for copy */}
      <Dialog open={!!freshCode} onOpenChange={() => setFreshCode(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Access code ready</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Beta code for <span className="font-mono">{freshCode?.email}</span>
              {freshCode?.email_sent
                ? " has been emailed. They'll receive it in the next few minutes."
                : " — copy and share this code manually since email is not configured."}
            </p>
            <div className="p-4 bg-slate-900 text-white rounded font-mono text-lg text-center tracking-wider break-all" data-testid="fresh-code-display">
              {freshCode?.code}
            </div>
            <Button onClick={() => copyCode(freshCode.code)} className="w-full bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Copy className="w-4 h-4 mr-2" /> Copy code
            </Button>
            <div className="text-[10px] font-mono text-slate-500 text-center">
              Single-use · expires {new Date(freshCode?.expires_at || Date.now()).toLocaleString()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
